import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  TemplateRef,
  viewChildren,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { PageIndexes } from '../pagination/pagination.component';
import { LoaderTheme } from '../loader/enums';

export type ClassOptions = string | string[] | Set<string> | Record<string, unknown> | null | undefined;

export type RowDataItem<T> = Record<
  string,
  {
    data: T;
    options?: {
      class?: ClassOptions;
      useBtn?: boolean;
      btnIcon?: SvgLibraryIcon;
      btnText?: string;
      ignorePadding?: boolean;
    };
  }
>;

export interface HeaderItem {
  key: string;
  value?: string;
  options?: {
    class?: ClassOptions;
    isMarked?: boolean;
  };
}

/**
 * Table component which works with different templates
 *
 * @example
 *  <fin-table
 *    [rowData]="rowData"
 *    [cellTemplate]="itemTemplate"
 *    [headers]="header"
 *    [headerTemplate]="headerTemplate"
 *   ></fin-table>
 */
@Component({
  selector: 'fcom-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableComponent<T> {
  protected readonly LoaderTheme = LoaderTheme;

  headers = input<HeaderItem[]>();
  rowData = input<Array<RowDataItem<T>>>();
  cellTemplate = input<TemplateRef<unknown>>();
  mobileHeader = input<TemplateRef<unknown>>();
  mobileTemplate = input<TemplateRef<unknown>>();
  usePagination = input(false);
  textBeforePages = input<string>();
  itemsPerPageOptions = input<number[]>([8, 16]);
  headerTemplate = input<TemplateRef<unknown>>();
  caption = input<string>();
  loading = input<boolean>();
  noResultsMsg = input<string>();

  clickBtn = output<T>();

  tableHeaders = viewChildren<ElementRef>('tableHeader');

  readonly tableData = signal<Array<RowDataItem<T>> | undefined>(undefined);
  readonly totalPaginationItems = computed(() => this.rowData()?.length ?? 0);

  constructor() {
    effect(() => {
      const rowData = this.rowData();
      const usePagination = this.usePagination();
      if (!usePagination) {
        this.tableData.set(rowData);
      } else {
        this.tableData.set(rowData?.slice(0, this.itemsPerPageOptions()[0]));
      }
    });
  }

  changeIndexes({ startIndex, endIndex }: PageIndexes): void {
    this.tableData.set(this.rowData()?.slice(startIndex, endIndex));
  }

  emitClick(data: T): void {
    this.clickBtn.emit(data);
  }
}
